import { useTranslation } from "react-i18next";
import type { SchemaElement, SchemaRegion } from "../../../api/restaurantSchema";
import { AddTableCard, TableCard } from "./TableCard";
import { seatGroups } from "./seatGroups";

const FilterChip = ({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    aria-pressed={active}
    onClick={onClick}
    className={`flex h-8 shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-[12.5px] font-semibold leading-[1.2] transition-colors duration-150 ${
      active
        ? "border border-brand/40 bg-brand-tint text-brand-deep-ink"
        : "border border-shade-3 bg-surface text-ink-2 hover:bg-fill"
    }`}
  >
    {label}
    <span className={`tabular-nums ${active ? "text-brand-deep-ink/75" : "text-muted"}`}>{count}</span>
  </button>
);

/**
 * Лявата карта: kicker с името на залата, „23 маси · 106 места", „Избери
 * всички" (видимите след търсене/филтър), чипове по брой места и мрежата от
 * плочки. Колоните следват ширината на КАРТАТА (container query) — на
 * телефон 2–3 по-малки плочки, на таблет 3–4, на десктоп 5+.
 */
const TablesGridCard = ({
  region,
  tables,
  visibleTables,
  selectedIds,
  seatFilter,
  isNarrow,
  hasSearch,
  onSeatFilterChange,
  onSelect,
  onToggle,
  onSelectAll,
  onClearSelection,
  onAddTable,
}: {
  region: SchemaRegion;
  // Всички маси на залата (за броячите) и видимите след търсене/филтър.
  tables: SchemaElement[];
  visibleTables: SchemaElement[];
  selectedIds: Set<number>;
  seatFilter: number | null;
  isNarrow: boolean;
  hasSearch: boolean;
  onSeatFilterChange: (seats: number | null) => void;
  onSelect: (table: SchemaElement) => void;
  onToggle: (table: SchemaElement) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onAddTable: () => void;
}) => {
  const { t } = useTranslation();
  const seatCount = tables.reduce((sum, table) => sum + (table.seats ?? 0), 0);
  const allVisibleSelected =
    visibleTables.length > 0 && visibleTables.every((table) => selectedIds.has(table.id));
  const groups = seatGroups(tables);
  const isFiltered = hasSearch || seatFilter !== null;

  const chips = groups.length > 0 && (
    <div
      className={`flex gap-1.5 ${
        isNarrow
          ? "-mx-3.5 overflow-x-auto px-3.5 pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          : "ml-auto min-w-0 flex-wrap justify-end"
      }`}
    >
      <FilterChip
        label={t("tablesConfig.grid.filterAll")}
        count={tables.length}
        active={seatFilter === null}
        onClick={() => onSeatFilterChange(null)}
      />
      {groups.map(([seats, count]) => (
        <FilterChip
          key={seats}
          label={t("restaurantSchema.counts.seats", { count: seats })}
          count={count}
          active={seatFilter === seats}
          onClick={() => onSeatFilterChange(seatFilter === seats ? null : seats)}
        />
      ))}
    </div>
  );

  return (
    <section className={`rounded-[16px] border border-shade-2 bg-surface ${isNarrow ? "px-3.5 py-4" : "px-5 py-5"}`}>
      {/* Широко: чиповете са вдясно от заглавието, а когато не се събират на
          един ред до него (тясна карта до панела) — падат на свой ред под него. */}
      <div className={`flex gap-3 ${isNarrow ? "flex-col" : "flex-wrap items-start justify-between"}`}>
        <div className="min-w-0 max-w-full">
          <div className="truncate text-[10.5px] font-bold uppercase leading-[1.2] tracking-[1.2px] text-muted">
            {t("tablesConfig.grid.kicker", { region: region.name })}
          </div>
          <div className="mt-1.5 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="text-[14px] font-bold leading-[1.3] tabular-nums text-ink">
              {t("restaurantSchema.counts.tables", { count: tables.length })} ·{" "}
              {t("restaurantSchema.counts.seats", { count: seatCount })}
            </span>
            {visibleTables.length > 0 && (
              <button
                type="button"
                onClick={allVisibleSelected ? onClearSelection : onSelectAll}
                className="cursor-pointer border-none bg-transparent p-0 text-[13px] font-semibold leading-[1.3] text-brand-deep-ink underline-offset-2 hover:underline"
              >
                {allVisibleSelected ? t("tablesConfig.bulk.cancel") : t("tablesConfig.grid.selectAll")}
              </button>
            )}
          </div>
        </div>
        {chips}
      </div>

      <div className="@container mt-4">
        {visibleTables.length === 0 && (
          <div className="mb-3 rounded-[12px] bg-fill px-4 py-3 text-[13px] leading-[1.5] text-body">
            {isFiltered ? t("tablesConfig.grid.noMatches") : t("tablesConfig.listEmpty")}
          </div>
        )}
        <div
          className={`grid grid-cols-2 @[300px]:grid-cols-3 @[500px]:grid-cols-4 @[660px]:grid-cols-5 @[900px]:grid-cols-6 @[1150px]:grid-cols-7 ${
            isNarrow ? "gap-2" : "gap-2.5"
          }`}
        >
          {visibleTables.map((table) => (
            <TableCard
              key={table.id}
              table={table}
              isSelected={selectedIds.has(table.id)}
              isCompact={isNarrow}
              onSelect={() => onSelect(table)}
              onToggle={() => onToggle(table)}
            />
          ))}
          {!isFiltered && <AddTableCard isCompact={isNarrow} onClick={onAddTable} />}
        </div>
      </div>
    </section>
  );
};

export default TablesGridCard;
